import React, { useEffect, useState } from 'react';
import '../styles/seasonal.css';
import SeasonalCountdown from './SeasonalCountdown';
import SeasonalLights from './ChristmasLights';
import SeasonalHeroSection from './SeasonalHeroSection';

// Saisonzyklus identisch zu SeasonalHeroSection.jsx / SeasonalCountdown.jsx
const SEASONS_CYCLE = [
  { key: 'spring', month: 2, day: 20 },
  { key: 'summer', month: 5, day: 21 },
  { key: 'autumn', month: 8, day: 23 },
  { key: 'winter', month: 11, day: 21 },
];

const resolveCurrentSeason = (now = new Date()) => {
  const y = now.getFullYear();
  const candidates = [];
  for (let i = -1; i <= 1; i++) {
    SEASONS_CYCLE.forEach((s) => {
      candidates.push({ key: s.key, date: new Date(y + i, s.month, s.day) });
    });
  }
  candidates.sort((a, b) => a.date - b.date);
  let current = null;
  for (const c of candidates) {
    if (c.date <= now) current = c;
    else break;
  }
  return current?.key || 'winter';
};

const SeasonalDecorations = () => {
  const [decorations, setDecorations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTheme, setActiveTheme] = useState('standard');

  // Hide on admin pages
  const isAdminPage = window.location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPage) {
      setLoading(false);
      return;
    }
    loadActiveDecorations();
  }, [isAdminPage]);

  const loadActiveDecorations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/seasonal/active`);
      if (response.ok) {
        const data = await response.json();

        // Set active theme for body class.
        // 'standard'/'auto'/leer → automatisch passende Saison aus dem Datum ableiten.
        const rawTheme = data.seasonalTheme || 'standard';
        const isSpecialEvent = ['christmas', 'newyear', 'easter', 'mothersday', 'valentines'].includes(rawTheme);
        const isExplicitSeason = ['spring', 'summer', 'autumn', 'winter'].includes(rawTheme);
        const themeName = isSpecialEvent || isExplicitSeason ? rawTheme : resolveCurrentSeason();
        setActiveTheme(themeName);

        // Add theme class to body
        document.body.classList.remove('theme-christmas', 'theme-newyear', 'theme-easter', 'theme-summer', 'theme-autumn', 'theme-spring', 'theme-winter', 'theme-valentines', 'theme-mothersday', 'theme-standard');
        document.body.classList.add(`theme-${themeName}`);

        if (data.decorations && Array.isArray(data.decorations)) {
          setDecorations(data.decorations);
        }
      }
    } catch (error) {
      console.error('Error loading decorations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  // Don't render on admin pages
  if (isAdminPage) {
    return null;
  }

  // Don't render if standard theme and no decorations
  if (activeTheme === 'standard' && decorations.length === 0) {
    return null;
  }

  return (
    <>
      {/* Seasonal Lights for all themes - with spacing below */}
      {activeTheme !== 'standard' && (
        <div className="mb-4">
          <SeasonalLights theme={activeTheme} />
        </div>
      )}
      
      {/* Seasonal Countdown for all themes */}
      <SeasonalCountdown theme={activeTheme} />
      
      {/* Festive Banner */}
      {activeTheme === 'christmas' && (
        <div className="w-full bg-gradient-to-r from-red-700 via-red-600 to-green-700 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">🎄 Frohe Weihnachten · festliche Kollektion 🎁</span>
        </div>
      )}

      {activeTheme === 'newyear' && (
        <div className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">🎉 Frohes Neues Jahr 🎆</span>
        </div>
      )}

      {activeTheme === 'easter' && (
        <div className="w-full bg-gradient-to-r from-pink-500 via-purple-400 to-pink-500 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">🐰 Frohe Ostern · Frühjahrskollektion 🌸</span>
        </div>
      )}

      {activeTheme === 'mothersday' && (
        <div className="w-full bg-gradient-to-r from-pink-600 via-rose-500 to-pink-600 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">💐 Alles Liebe zum Muttertag 💝</span>
        </div>
      )}

      {activeTheme === 'valentines' && (
        <div className="w-full bg-gradient-to-r from-red-600 via-pink-500 to-red-600 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">💕 Happy Valentine's Day 💝</span>
        </div>
      )}

      {activeTheme === 'summer' && (
        <div className="w-full bg-gradient-to-r from-yellow-500 via-orange-400 to-yellow-500 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">☀️ Sommer-Kollektion · für sonnige Tage 🏖️</span>
        </div>
      )}
      
      {activeTheme === 'autumn' && (
        <div className="w-full bg-gradient-to-r from-[#E9C6AA] via-[#F1D5BE] to-[#E9C6AA] text-[#5D4E3C] py-1.5 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">🍂 Herbst-Kollektion · gemütlich 🍁</span>
        </div>
      )}

      {activeTheme === 'spring' && (
        <div className="w-full bg-gradient-to-r from-pink-400 via-rose-300 to-yellow-300 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">🌷 Frühlings-Kollektion · frisch & zart 🌸</span>
        </div>
      )}

      {activeTheme === 'winter' && (
        <div className="w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-slate-600 text-white py-2 text-center text-xs sm:text-sm font-medium pointer-events-auto z-40 relative">
          <span className="inline-flex items-center gap-1.5">❄️ Winter-Kollektion · kuschelig & warm ⛄</span>
        </div>
      )}
      
      {/* Decorations - Multiple layers for full page coverage */}
      <>
        {[0, 1, 2, 3].map((layer) => (
          <div 
            key={`decoration-layer-${layer}`}
            className="fixed top-0 left-0 w-full h-screen pointer-events-none z-10 overflow-hidden hidden md:block"
            style={{
              transform: `translateY(${layer * 100}vh)`
            }}
          >
            {decorations.map((decoration) => {
              // Use percentage for Y position so decorations spread across viewport
              const yPosition = decoration.position.y;
              
              // Responsive size - smaller on mobile
              const responsiveSize = `clamp(${decoration.size * 0.6}px, ${decoration.size * 0.8}vw, ${decoration.size}px)`;
              
              // Slight variation in position for each layer
              const xVariation = (decoration.position.x + (layer * 7)) % 100;
              
              return (
                <div
                  key={`${decoration.instanceId}-layer-${layer}`}
                  className="absolute animate-float"
                  style={{
                    left: `${xVariation}%`,
                    top: `${yPosition}%`,
                    fontSize: responsiveSize,
                    transform: `translate(-50%, -50%) rotate(${decoration.rotation + (layer * 15)}deg)`,
                    opacity: decoration.opacity * 0.9,
                    userSelect: 'none',
                    animation: `float ${4 + Math.random() * 4}s ease-in-out infinite`,
                    animationDelay: `${Math.random() * 3 + layer * 0.5}s`
                  }}
                >
                  {decoration.emoji}
                </div>
              );
            })}
          </div>
        ))}

        <style>{`
          @keyframes float {
            0%, 100% {
              transform: translate(-50%, -50%) translateY(0px) rotate(var(--rotation, 0deg));
            }
            50% {
              transform: translate(-50%, -50%) translateY(-10px) rotate(var(--rotation, 0deg));
            }
          }
          
          @media (max-width: 768px) {
            .animate-float {
              font-size: 0.7em !important;
            }
          }
        `}</style>
      </>
    </>
  );
};

export default SeasonalDecorations;
